import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProdutoCard from "./ProdutoCard";
import { Button, Select, Spinner, TextInput } from "flowbite-react";
import Masonry from "react-masonry-css";
import { HiOutlineSearch } from "react-icons/hi";
import { FaTimes, FaWhatsapp } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import ProdutoModal from "./ProdutoModal";
import LinkChoiceModal from "./LinkChoiceModal";
import {
  getNudgesCatalogo,
  getProdutoPublicoById,
  getProdutosFiltradosPaginado,
  registrarAcao,
} from "../../api/public";
import { getSessionId } from "../../utils";
import { useSelector } from "react-redux";
import {
  buildLinkHref,
  getActionTypeByLinkType,
  getLinksByType,
  isUuid,
} from "../../utils/storeLinks";

const DISCOVERY_TRIGGER_SCROLLS = 50;
const AVAILABILITY_TRIGGER_SCROLLS = 150;
const SCROLL_STEP_PX = 300;
const CATALOG_NUDGE_VISIBILITY_EVENT = "vintree:catalog-nudge-visibility";

const GridCatalogo = ({ tags = [], contarFotoVisualizada, username }) => {
  const [tagSelecionada, setTagSelecionada] = useState("todas");
  const [busca, setBusca] = useState("");
  const [produtos, setProdutos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [page, setPage] = useState(1);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const loja = useSelector((state) => state.catalogo.loja);
  const sessionId = getSessionId();
  const [mostrarSpinner, setMostrarSpinner] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [scrollSteps, setScrollSteps] = useState(0);
  const [showDiscoveryNudge, setShowDiscoveryNudge] = useState(false);
  const [showAvailabilityNudge, setShowAvailabilityNudge] = useState(false);
  const [nudgeTags, setNudgeTags] = useState([]);
  const [nudgeSelectedTag, setNudgeSelectedTag] = useState("");
  const [nudgeTerms, setNudgeTerms] = useState([]);
  const [nudgesLoading, setNudgesLoading] = useState(false);
  const [discoveryStep, setDiscoveryStep] = useState("tags");
  const [linkPickerState, setLinkPickerState] = useState({
    open: false,
    title: "",
    links: [],
    origem: null,
    whatsappText: null,
  });

  const prevParams = useRef({ tagSelecionada: null, busca: null });
  const lastScrollY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const filtrosKeyRef = useRef("");

  const safeTags = useMemo(() => (Array.isArray(tags) ? tags : []), [tags]);

  const discoverySessionKey = `vintree_discovery_nudge_done_${username}`;
  const availabilitySessionKey = `vintree_old_products_nudge_done_${username}`;

  const hasBuscaOrTagFiltro =
    tagSelecionada !== "todas" || (busca && busca.trim().length > 0);

  const whatsappLinks = getLinksByType(loja, "whatsapp");
  const disponibilidadeMensagem =
    "Oi! Vi itens mais antigos no catalogo e queria confirmar disponibilidade atual.";
  const disponibilidadeWhatsappLink =
    whatsappLinks.length === 1
      ? buildLinkHref(whatsappLinks[0], { whatsappText: disponibilidadeMensagem })
      : null;

  const trackLinkClick = useCallback(
    (link, origem) => {
      if (!loja) return;

      const payload = {
        session_id: sessionId,
        loja_id: loja.id,
        tipo_acao: getActionTypeByLinkType(link?.tipo),
        origem: origem || "grid",
        link_tipo: link?.tipo || null,
        link_label: link?.label || null,
        link_valor: link?.valor || null,
      };

      if (isUuid(link?.id)) {
        payload.loja_link_id = link.id;
      }

      registrarAcao(payload).catch(console.error);
    },
    [loja, sessionId]
  );

  const marcarDiscoveryComoVisto = () => {
    sessionStorage.setItem(discoverySessionKey, "1");
  };

  const marcarAvailabilityComoVisto = () => {
    sessionStorage.setItem(availabilitySessionKey, "1");
  };

  const aplicarFiltroInteligente = (categoria, termo = "") => {
    const categoriaFinal = categoria || "todas";
    const termoFinal = termo || "";

    // Sobe imediatamente para o topo antes de aplicar os novos filtros.
    window.scrollTo(0, 0);
    lastScrollY.current = 0;

    requestAnimationFrame(() => {
      setTagSelecionada(categoriaFinal);
      setBusca(termoFinal);

      setSearchParams((params) => {
        const p = new URLSearchParams(params);
        p.set("categoria", categoriaFinal);

        if (termoFinal) p.set("busca", termoFinal);
        else p.delete("busca");

        p.delete("produto");
        return p;
      });
    });
  };

  const carregarNudges = useCallback(
    async (tagNome) => {
      if (!username) return;

      setNudgesLoading(true);
      try {
        const data = await getNudgesCatalogo(
          username,
          tagNome ? { tag: tagNome } : {}
        );
        setNudgeTags(data?.popularTags || []);
        setNudgeSelectedTag(data?.selectedTag || "");
        setNudgeTerms(data?.frequentTerms || []);
      } catch (error) {
        console.error("Erro ao carregar nudges:", error);
        setNudgeTags([]);
        setNudgeTerms([]);
      } finally {
        setNudgesLoading(false);
      }
    },
    [username]
  );

  useEffect(() => {
    const novaCategoria = searchParams.get("categoria") || "todas";
    const novaBusca = searchParams.get("busca") || "";

    setTagSelecionada(novaCategoria);
    setBusca(novaBusca);
  }, [searchParams]);

  useEffect(() => {
    const produtoId = searchParams.get("produto");
    if (!produtoId) return;

    const produtoExistente = produtos.find((p) => p.id === produtoId);
    if (produtoExistente) {
      setProdutoSelecionado(produtoExistente);
      return;
    }

    if (!loja) return;

    getProdutoPublicoById(produtoId, loja.id)
      .then((produto) => {
        if (!produto) return;

        setProdutoSelecionado(produto);

        setProdutos((prev) => {
          const existe = prev.some((p) => p.id === produto.id);
          return existe ? prev : [produto, ...prev];
        });
      })
      .catch((err) => {
        console.error("Erro ao buscar produto direto do backend:", err.message);
      });
  }, [searchParams, loja, produtos]);

  const carregarMaisProdutos = useCallback(async () => {
    if (carregando || !hasMore || !loja) return;

    const filtrosKeyAtual = filtrosKeyRef.current;
    setCarregando(true);
    setMostrarSpinner(true);

    try {
      const resultado = await getProdutosFiltradosPaginado(username, {
        page,
        tag: tagSelecionada,
        busca,
      });

      if (filtrosKeyAtual !== filtrosKeyRef.current) {
        return;
      }

      const novos = resultado.produtos || [];
      const totalPagesFromApi = Number(resultado.totalPages) || 0;
      const currentPageFromApi = Number(resultado.currentPage) || page;

      setProdutos((prev) => {
        if (page === 1) {
          if (contarFotoVisualizada) novos.forEach(() => contarFotoVisualizada());
          return novos;
        }

        const existentes = new Set(prev.map((p) => p.id));
        const unicos = novos.filter((p) => !existentes.has(p.id));
        if (contarFotoVisualizada) unicos.forEach(() => contarFotoVisualizada());
        return [...prev, ...unicos];
      });

      const chegouAoFim =
        totalPagesFromApi === 0 ||
        currentPageFromApi >= totalPagesFromApi ||
        novos.length === 0;

      if (chegouAoFim) {
        setHasMore(false);
      } else {
        setHasMore(true);
        setPage(currentPageFromApi + 1);
      }
    } catch (err) {
      console.error("Erro ao carregar produtos:", err);
    } finally {
      setCarregando(false);
      setMostrarSpinner(false);
    }
  }, [
    busca,
    carregando,
    contarFotoVisualizada,
    hasMore,
    loja,
    page,
    tagSelecionada,
    username,
  ]);

  useEffect(() => {
    if (!loja) return;

    if (
      prevParams.current.tagSelecionada === tagSelecionada &&
      prevParams.current.busca === busca
    ) {
      return;
    }

    setProdutos([]);
    setPage(1);
    setHasMore(true);
    setMostrarSpinner(false);
    setScrollSteps(0);
    filtrosKeyRef.current = `${tagSelecionada}|${busca.trim()}`;

    const isPrimeiroCarregamento =
      prevParams.current.tagSelecionada === null && prevParams.current.busca === null;
    const delay = isPrimeiroCarregamento ? 0 : 200;

    const timer = setTimeout(() => {
      carregarMaisProdutos();

      if (tagSelecionada !== "todas") {
        registrarAcao({
          session_id: sessionId,
          loja_id: loja.id,
          tipo_acao: "filtrar",
          tag: tagSelecionada,
          origem: "grid",
        }).catch(console.error);
      }

      if (busca.trim()) {
        registrarAcao({
          session_id: sessionId,
          loja_id: loja.id,
          tipo_acao: "buscar",
          termo_busca: busca.trim(),
          origem: "grid",
        }).catch(console.error);
      }
    }, delay);

    prevParams.current = { tagSelecionada, busca };

    return () => clearTimeout(timer);
  }, [busca, carregarMaisProdutos, loja, sessionId, tagSelecionada]);

  useEffect(() => {
    const sentinela = document.getElementById("sentinela");
    if (!sentinela || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !carregando && hasMore) {
          carregarMaisProdutos();
        }
      },
      { rootMargin: "100px" }
    );

    observer.observe(sentinela);

    return () => observer.disconnect();
  }, [carregando, carregarMaisProdutos, hasMore, produtos]);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      if (delta >= SCROLL_STEP_PX) {
        const steps = Math.floor(delta / SCROLL_STEP_PX);
        setScrollSteps((prev) => prev + steps);
        lastScrollY.current = currentY;
      } else if (delta < 0) {
        lastScrollY.current = currentY;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!loja || hasBuscaOrTagFiltro) return;

    const discoveryDone = sessionStorage.getItem(discoverySessionKey) === "1";
    const availabilityDone = sessionStorage.getItem(availabilitySessionKey) === "1";

    if (
      !discoveryDone &&
      scrollSteps >= DISCOVERY_TRIGGER_SCROLLS &&
      !showDiscoveryNudge
    ) {
      setShowDiscoveryNudge(true);
      setDiscoveryStep("tags");
      carregarNudges();
      return;
    }

    if (
      !availabilityDone &&
      scrollSteps >= AVAILABILITY_TRIGGER_SCROLLS &&
      !showDiscoveryNudge &&
      !showAvailabilityNudge
    ) {
      setShowAvailabilityNudge(true);
    }
  }, [
    availabilitySessionKey,
    carregarNudges,
    discoverySessionKey,
    hasBuscaOrTagFiltro,
    scrollSteps,
    loja,
    showAvailabilityNudge,
    showDiscoveryNudge,
  ]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(CATALOG_NUDGE_VISIBILITY_EVENT, {
        detail: {
          visible: showDiscoveryNudge || showAvailabilityNudge,
        },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent(CATALOG_NUDGE_VISIBILITY_EVENT, {
          detail: { visible: false },
        })
      );
    };
  }, [showAvailabilityNudge, showDiscoveryNudge]);

  const breakpointColumnsObj = {
    default: 4,
    1024: 3,
    640: 2,
    0: 1,
  };

  const skeletonHeights = [260, 330, 300, 380, 280, 360, 320, 400];
  const mostrarSkeletonGrid = carregando && produtos.length === 0;

  return (
    <div className="w-full max-w-[1600px] mx-auto px-2 sm:px-4 pb-20">
      <div className="flex flex-col items-center gap-2 mb-4 w-full max-w-3xl mx-auto">
        <div className="w-full">
          <Select
            value={tagSelecionada}
            onChange={(e) => {
              const nova = e.target.value;
              setTagSelecionada(nova);
              setSearchParams((params) => {
                const p = new URLSearchParams(params);
                p.set("categoria", nova);
                return p;
              });
            }}
            className="w-full"
          >
            <option value="todas">Todas as categorias</option>
            {safeTags.map((tag) => (
              <option key={tag.id} value={tag.nome}>
                {tag.nome}
              </option>
            ))}
          </Select>
        </div>

        <div className="w-full">
          <TextInput
            icon={HiOutlineSearch}
            placeholder="O que você está procurando?"
            value={busca}
            onChange={(e) => {
              const novo = e.target.value;
              setBusca(novo);
              setSearchParams((params) => {
                const p = new URLSearchParams(params);
                if (novo) p.set("busca", novo);
                else p.delete("busca");
                return p;
              });
            }}
            className="w-full"
          />
        </div>
      </div>

      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="flex gap-4"
        columnClassName="flex flex-col gap-4"
      >
        {mostrarSkeletonGrid
          ? skeletonHeights.map((height, idx) => (
              <div
                key={`skeleton-${idx}`}
                className="w-full bg-gray-200 rounded-xl shadow-sm animate-pulse"
                style={{ height }}
              />
            ))
          : produtos.map((produto) => (
              <ProdutoCard
                key={`${produto.id}-${tagSelecionada}`}
                produto={produto}
                aoClicar={() => {
                  setProdutoSelecionado(produto);
                  setSearchParams((params) => {
                    const p = new URLSearchParams(params);
                    p.set("produto", produto.id);
                    return p;
                  });
                  if (loja && sessionId) {
                    registrarAcao({
                      session_id: sessionId,
                      loja_id: loja.id,
                      tipo_acao: "ver_produto",
                      produto_id: produto.id,
                      origem: "grid",
                    }).catch(console.error);
                  }
                }}
              />
            ))}
      </Masonry>

      {produtoSelecionado && (
        <ProdutoModal
          produto={produtoSelecionado}
          onClose={() => {
            setProdutoSelecionado(null);
            setSearchParams((params) => {
              const p = new URLSearchParams(params);
              p.delete("produto");
              return p;
            });
          }}
        />
      )}

      {mostrarSpinner && hasMore && !mostrarSkeletonGrid && (
        <div className="flex justify-center mt-6">
          <Spinner aria-label="Carregando fotos" size="xl" />
        </div>
      )}

      {!mostrarSpinner && !hasMore && produtos.length > 0 && (
        <div className="text-center text-gray-400 mt-6 text-sm">Fim do catalogo.</div>
      )}

      {!mostrarSpinner && !hasMore && produtos.length === 0 && (
        <div className="text-center text-gray-400 mt-6 text-sm">
          Nenhum produto encontrado com esse filtro.
        </div>
      )}

      {hasMore && <div id="sentinela" className="h-8"></div>}

      {showDiscoveryNudge && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[95%] max-w-xl z-40 bg-white border border-gray-200 rounded-2xl shadow-2xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Quer achar algo mais rapido?
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Escolha uma categoria popular para filtrar seu catalogo.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowDiscoveryNudge(false);
                marcarDiscoveryComoVisto();
              }}
              className="text-gray-400 hover:text-gray-700"
              aria-label="Fechar sugestao"
            >
              <FaTimes />
            </button>
          </div>

          {nudgesLoading ? (
            <div className="py-4 flex justify-center">
              <Spinner size="md" />
            </div>
          ) : (
            <>
              {discoveryStep === "tags" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {nudgeTags.map((item) => (
                    <Button
                      key={item.nome}
                      size="xs"
                      color="light"
                      pill
                      onClick={async () => {
                        setNudgeSelectedTag(item.nome);
                        setDiscoveryStep("terms");
                        await carregarNudges(item.nome);
                      }}
                    >
                      {item.nome}
                    </Button>
                  ))}
                </div>
              )}

              {discoveryStep === "terms" && (
                <div className="mt-3">
                  <p className="text-xs text-gray-600 mb-2">
                    Categoria: <strong>{nudgeSelectedTag}</strong>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {nudgeTerms.length > 0 ? (
                      nudgeTerms.map((term) => (
                        <Button
                          key={term}
                          size="xs"
                          color="purple"
                          pill
                          onClick={() => {
                            aplicarFiltroInteligente(nudgeSelectedTag, term);
                            setShowDiscoveryNudge(false);
                            marcarDiscoveryComoVisto();
                          }}
                        >
                          {term}
                        </Button>
                      ))
                    ) : (
                      <Button
                        size="xs"
                        color="purple"
                        pill
                        onClick={() => {
                          aplicarFiltroInteligente(nudgeSelectedTag);
                          setShowDiscoveryNudge(false);
                          marcarDiscoveryComoVisto();
                        }}
                      >
                        Ver produtos dessa categoria
                      </Button>
                    )}
                  </div>

                  <div className="mt-3 flex gap-2">
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => setDiscoveryStep("tags")}
                    >
                      Voltar
                    </Button>
                    <Button
                      size="xs"
                      color="gray"
                      onClick={() => {
                        setShowDiscoveryNudge(false);
                        marcarDiscoveryComoVisto();
                      }}
                    >
                      Agora nao
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showAvailabilityNudge && !showDiscoveryNudge && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[95%] max-w-xl z-40 bg-amber-50 border border-amber-200 rounded-2xl shadow-2xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Voce chegou em itens mais antigos.
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Alguns podem nao estar disponiveis. Confirme no WhatsApp para agilizar.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowAvailabilityNudge(false);
                marcarAvailabilityComoVisto();
              }}
              className="text-amber-500 hover:text-amber-700"
              aria-label="Fechar aviso"
            >
              <FaTimes />
            </button>
          </div>

          {disponibilidadeWhatsappLink && (
            <div className="mt-3">
              <Button
                color="success"
                className="w-full sm:w-auto"
                onClick={() => {
                  setShowAvailabilityNudge(false);
                  marcarAvailabilityComoVisto();

                  if (!whatsappLinks.length) return;

                  if (whatsappLinks.length === 1) {
                    trackLinkClick(whatsappLinks[0], "nudge_disponibilidade");
                    window.open(
                      disponibilidadeWhatsappLink,
                      "_blank",
                      "noopener,noreferrer"
                    );
                    return;
                  }

                  setLinkPickerState({
                    open: true,
                    title: "Escolha um WhatsApp",
                    links: whatsappLinks,
                    origem: "nudge_disponibilidade",
                    whatsappText: disponibilidadeMensagem,
                  });
                }}
              >
                <FaWhatsapp className="mr-2" />
                Confirmar no WhatsApp
              </Button>
            </div>
          )}
        </div>
      )}

      <LinkChoiceModal
        open={linkPickerState.open}
        onClose={() =>
          setLinkPickerState({
            open: false,
            title: "",
            links: [],
            origem: null,
            whatsappText: null,
          })
        }
        title={linkPickerState.title}
        links={linkPickerState.links}
        onSelect={(link) => {
          const href = buildLinkHref(link, {
            whatsappText: linkPickerState.whatsappText,
          });
          if (!href) return;

          trackLinkClick(link, linkPickerState.origem || "grid");
          window.open(href, "_blank", "noopener,noreferrer");

          setLinkPickerState({
            open: false,
            title: "",
            links: [],
            origem: null,
            whatsappText: null,
          });
        }}
      />
    </div>
  );
};

export default GridCatalogo;
